import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useQuery } from 'react-query'
import { companyService } from '@/services/company'
import { servicesService } from '@/services/services'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/contexts/LanguageContext'
import { useTranslation } from 'react-i18next'
import { TurkeyFlag, USAFlag } from '@/components/ui/Flags'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isLangOpen, setIsLangOpen] = useState(false)
  const location = useLocation()
  const { language, setLanguage } = useLanguage()
  const { t } = useTranslation()

  // Fetch company info for logo
  const { data: companyInfo } = useQuery('companyInfo', companyService.getCompanyInfo, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch services for dropdown
  const { data: services = [] } = useQuery(
    'services',
    () => servicesService.getServices({ is_active: true }),
    {
      staleTime: 5 * 60 * 1000,
    }
  )

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
    setIsServicesOpen(false)
    setIsLangOpen(false)
  }, [location])

  // Language options with flags
  const languages = [
    { code: 'tr', name: 'Türkçe', FlagComponent: TurkeyFlag },
    { code: 'en', name: 'English', FlagComponent: USAFlag }
  ]

  const currentLang = languages.find(lang => lang.code === language) || languages[0]
  const CurrentFlagComponent = currentLang.FlagComponent

  // Create dynamic navigation with services submenu
  const navigation = [
    { name: t('header.home'), href: '/' },
    { name: t('header.about'), href: '/hakkimizda' },
    { 
      name: t('header.services'), 
      href: '/hizmetler',
      submenu: services.map(service => ({
        name: service.title,
        href: `/hizmetler/${service.slug || service.id}`
      }))
    },
    { name: t('header.projects'), href: '/projeler' },
    { name: t('header.blog'), href: '/blog' },
    { name: t('header.contact'), href: '/iletisim' },
  ]

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/90 backdrop-blur-md shadow-lg border-b border-blue-500/20' : 'bg-black/80 backdrop-blur-sm'
    }`}>
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/logo.png" 
              alt={companyInfo?.name || 'CRV Software'} 
              className={`w-24 h-24 lg:w-32 lg:h-32 rounded-lg transition-all duration-300 ${
                location.pathname === '/' ? 'group-hover:shadow-lg group-hover:shadow-blue-500/50' : ''
              }`}
            />
            <span className={`text-xl lg:text-2xl font-display font-bold text-white transition-colors duration-300 ${
              location.pathname === '/' ? 'group-hover:text-blue-400' : ''
            }`}>
              {companyInfo?.name || 'CRV Software'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.submenu ? (
                  <div
                    className="flex items-center space-x-1 relative group"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <Link 
                      to={item.href}
                      className="text-white hover:text-blue-400 transition-all duration-300 relative z-10"
                    >
                      {item.name}
                    </Link>
                    <ChevronDown className="w-4 h-4 text-white group-hover:rotate-180 group-hover:text-blue-400 transition-all duration-300" />
                    {/* Hover glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className="text-white hover:text-blue-400 transition-all duration-300 relative group"
                  >
                    <span className="relative z-10">{item.name}</span>
                    {/* Hover glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                  </Link>
                )}

                {/* Services Dropdown */}
                {item.submenu && (
                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-black/90 backdrop-blur-md rounded-lg shadow-lg border border-blue-500/30 py-2"
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                      >
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-4 py-2 text-sm text-white hover:bg-blue-500/20 hover:text-blue-400 transition-all duration-300 relative group"
                          >
                            <span className="relative z-10">{subItem.name}</span>
                            {/* Hover glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
            
            {/* Language Switcher - Desktop - EN SAĞDA */}
            <div className="relative ml-4"
              onMouseEnter={() => setIsLangOpen(true)}
              onMouseLeave={() => setIsLangOpen(false)}
            >
              <button className="flex items-center space-x-2 px-3 py-2 text-white hover:text-blue-400 transition-all duration-300 relative group bg-white/5 rounded-lg border border-blue-500/30 hover:border-blue-400/50">
                <CurrentFlagComponent className="w-5 h-4 rounded-sm shadow-sm" />
                <span className="text-sm font-semibold">{currentLang.code.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3 group-hover:rotate-180 transition-all duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </button>
              
              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full right-0 mt-2 w-36 bg-black/90 backdrop-blur-md rounded-lg shadow-lg border border-blue-500/30 py-1 overflow-hidden"
                  >
                    {languages.map((lang) => {
                      const FlagComp = lang.FlagComponent
                      return (
                        <button
                          key={lang.code}
                          onClick={() => setLanguage(lang.code as 'tr' | 'en')}
                          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all duration-300 ${
                            language === lang.code 
                              ? 'bg-blue-500/30 text-blue-300 font-semibold' 
                              : 'text-white hover:bg-blue-500/20 hover:text-blue-400'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <FlagComp className="w-5 h-4 rounded-sm shadow-sm" />
                            <span>{lang.name}</span>
                          </div>
                          <span className="text-xs font-bold opacity-60">{lang.code.toUpperCase()}</span>
                        </button>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-white hover:text-blue-400 transition-all duration-300 relative group"
          >
            <span className="relative z-10">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </span>
            {/* Hover glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-black/90 backdrop-blur-md border-t border-blue-500/30"
            >
              <div className="py-4 space-y-2">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.submenu ? (
                      <div className="space-y-1">
                        <Link 
                          to={item.href}
                          className="block px-4 py-2 text-white font-medium hover:bg-blue-500/20 hover:text-blue-400 transition-all"
                        >
                          {item.name}
                        </Link>
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-6 py-2 text-sm text-gray-300 hover:bg-blue-500/20 hover:text-blue-400 transition-all duration-300 relative group"
                          >
                            <span className="relative z-10">{subItem.name}</span>
                            {/* Hover glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className="block px-4 py-2 text-white hover:bg-blue-500/20 hover:text-blue-400 transition-all duration-300 relative group"
                      >
                        <span className="relative z-10">{item.name}</span>
                        {/* Hover glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </Link>
                    )}
                  </div>
                ))}
                {/* Language Switcher - Mobile */}
                <div className="px-4 pt-2 pb-2 border-t border-blue-500/30">
                  <div className="flex items-center justify-center space-x-3">
                    {languages.map((lang) => {
                      const FlagComp = lang.FlagComponent
                      return (
                        <button
                          key={lang.code}
                          onClick={() => setLanguage(lang.code as 'tr' | 'en')}
                          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                            language === lang.code
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white border border-blue-400 shadow-lg shadow-blue-500/30'
                              : 'bg-white/5 text-gray-300 border border-blue-500/30 hover:bg-blue-500/20 hover:text-white'
                          }`}
                        >
                          <FlagComp className="w-6 h-5 rounded-sm shadow-md" />
                          <span className="font-semibold">{lang.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                
                <div className="px-4 pt-4">
                  <Link
                    to="/iletisim"
                    className="relative inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all duration-300 group overflow-hidden"
                  >
                    <span className="relative z-10">{t('header.contactCTA')}</span>
                    {/* Animated border */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                    {/* Rotating border effect */}
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  )
}

export default Header
